import { STORAGE } from '@webready/shared/config';
import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { AppSettings } from '@webready/shared/schema';
import { hexToHsl } from '@/lib/utils';
import { getSchemaScopedQueryKey } from '@/lib/schemaStorage';
import { cacheDesignCustomFlag } from '@/lib/designCustomCache';
import staticAppSettings from '@/constants/app-settings.json';



// Helper to determine text color based on background
const getContrastingTextColor = (hex: string): string => {
  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return (yiq >= 128) ? '0 0% 0%' : '0 0% 100%'; // Return HSL for black or white
};

// Default theme colors (light mode)
// Note: --primary uses neutral gray to prevent FOUC (Flash of Unstyled Content)
// The actual brand color will be applied when settings load from API
const defaultTheme = {
  '--background': '0 0% 100%',
  '--foreground': '222.2 84% 4.9%',
  '--card': '0 0% 100%',
  '--card-foreground': '222.2 84% 4.9%',
  '--popover': '0 0% 100%',
  '--popover-foreground': '222.2 84% 4.9%',
  '--primary': '220 13% 69%',           // Neutral gray - synced with index.html critical CSS
  '--primary-foreground': '0 0% 100%',  // White for contrast - synced with index.html critical CSS
  '--secondary': '210 40% 96.1%',
  '--secondary-foreground': '222.2 47.4% 11.2%',
  '--muted': '210 40% 96.1%',
  '--muted-foreground': '215.4 16.3% 46.9%',
  '--accent': '210 40% 96.1%',
  '--accent-foreground': '222.2 47.4% 11.2%',
  '--destructive': '0 84.2% 60.2%',
  '--destructive-foreground': '210 40% 98%',
  '--border': '214.3 31.8% 91.4%',
  '--input': '214.3 31.8% 91.4%',
  '--ring': '220 13% 69%',              // Match primary for consistent focus rings
  '--radius': '0.5rem',
};

interface AppSettingsContextType {
  settings: AppSettings | null;
  isLoading: boolean;
}

const AppSettingsContext = createContext<AppSettingsContextType | undefined>(undefined);

// Default app settings for placeholderData
// Loaded from static JSON fallback (exact sync with current DB) to prevent blocking & FOUC
const defaultAppSettings: AppSettings = staticAppSettings as unknown as AppSettings;

export function AppSettingsProvider({ children }: { children: ReactNode }) {
  const { data: settings, isLoading, isSuccess } = useQuery<AppSettings>({
    queryKey: getSchemaScopedQueryKey(['app-settings']),
    queryFn: async () => {
      const response = await api.get('app-settings');
      return response.data;
    },
    staleTime: 1000 * 60 * 60, // 1 hour - settings don't change often
    placeholderData: defaultAppSettings as AppSettings, // Use default values to prevent blocking
    gcTime: 1000 * 60 * 60 * 2, // 2 hours - keep in cache longer
  });

  useEffect(() => {
    const root = document.documentElement;

    // Apply default theme initially
    Object.entries(defaultTheme).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Use fetched settings if available, otherwise fallback to static fallback settings
    const activeSettings = settings || defaultAppSettings;

    // Apply active brand color immediately (resolving delay or FOUC)
    if (activeSettings?.brand_color) {
      const primaryHsl = hexToHsl(activeSettings.brand_color);
      const primaryForegroundHsl = getContrastingTextColor(activeSettings.brand_color);
      root.style.setProperty('--primary', primaryHsl);
      root.style.setProperty('--primary-foreground', primaryForegroundHsl);
      root.style.setProperty('--ring', primaryHsl);

      // Save to localStorage for instant index.html preloading
      try {
        localStorage.setItem(STORAGE.keys.themePrimaryHsl, primaryHsl);
        localStorage.setItem(STORAGE.keys.themePrimaryFgHsl, primaryForegroundHsl);
      } catch (e) {
        /* ignore */
      }
    }

    // Mark theme as applied so footer can show with the correct brand color
    // This is used by critical CSS in index.html to control footer visibility
    document.body.classList.add('theme-applied');

    if (activeSettings) {
      cacheDesignCustomFlag(activeSettings.design_custom === true);
    }
  }, [settings]);

  const value = { settings: settings || null, isLoading };

  return (
    <AppSettingsContext.Provider value={value}>
      {children}
    </AppSettingsContext.Provider>
  );
}

export function useAppSettings() {
  const context = useContext(AppSettingsContext);
  if (context === undefined) {
    throw new Error('useAppSettings must be used within an AppSettingsProvider');
  }
  return context;
}

// Hook pentru SEO settings
export const useSEOSettings = () => {
  const { settings } = useAppSettings();
  return {
    appName: settings?.app_name || '',
    metaTitle: settings?.meta_title || settings?.app_name || '',
    metaDescription: settings?.meta_description || '',
    metaKeywords: settings?.meta_keywords || '',
    faviconUrl: settings?.favicon_url,
    canonicalUrl: settings?.canonical_url,
    robotsDirectives: settings?.robots_directives || 'index, follow',
    ogTitle: settings?.og_title || settings?.meta_title || settings?.app_name || '',
    ogDescription: settings?.og_description || settings?.meta_description || '',
    ogImageUrl: settings?.og_image_url,
  };
};

// Hook pentru branding settings
export const useBrandingSettings = () => {
  const { settings } = useAppSettings();
  return {
    appName: settings?.app_name || '',
    tagline: settings?.tagline,
    companyDescription: settings?.company_description,
    contactEmail: settings?.contact_email,
    contactPhone: settings?.contact_phone,
    address: settings?.address,
    socialMedia: settings?.social_media || {},
  };
}; 
